/**
 * Design-system styling for the labor standards page and its form dialog.
 * Shared so every control across the page reads as one system: hairline
 * rest state, small radius, brick on focus.
 */
import { planner } from "../../planner-ui";

export const fieldSx = {
	bgcolor: planner.surface,
	"& .MuiOutlinedInput-root": {
		fontFamily: planner.fontUi,
		borderRadius: `${planner.radiusSm}px`,
		"& fieldset": { borderColor: planner.hairlineStrong },
		"&:hover fieldset": { borderColor: planner.inkMuted },
		"&.Mui-focused fieldset": {
			borderColor: planner.accent,
			borderWidth: `${planner.borderWidth}px`,
		},
	},
	"& .MuiInputLabel-root": {
		fontFamily: planner.fontUi,
		color: planner.inkSecondary,
		"&.Mui-focused": { color: planner.accent },
	},
} as const;

// Same control, with mono figures for machine data (minutes, counts, batches).
export const monoFieldSx = {
	...fieldSx,
	"& .MuiOutlinedInput-root": {
		...fieldSx["& .MuiOutlinedInput-root"],
		fontFamily: planner.fontMono,
		fontVariantNumeric: "tabular-nums",
	},
} as const;

export const menuItemSx = { fontFamily: planner.fontUi, fontSize: 13 } as const;

export const tableHeadCellSx = {
	py: 0.5,
	...planner.labelCaps,
	fontFamily: planner.fontUi,
	color: planner.inkSecondary,
	borderBottomColor: planner.hairline,
} as const;

export const tableCellSx = {
	py: 0.5,
	...planner.type.meta,
	fontFamily: planner.fontUi,
	color: planner.ink,
	borderBottomColor: planner.hairline,
} as const;

export const monoTableCellSx = {
	...tableCellSx,
	fontFamily: planner.fontMono,
	fontVariantNumeric: "tabular-nums",
} as const;

// Category tag: the operation's OperationType, shown as quiet context beside the
// operation's own name. Same hairline-on-alloy recipe as the phase pills.
export const categoryTagSx = {
	...planner.labelCaps,
	fontFamily: planner.fontUi,
	fontSize: 10,
	display: "inline-flex",
	alignItems: "center",
	px: 0.6,
	py: 0.2,
	borderRadius: `${planner.radiusXs}px`,
	border: `1px solid ${planner.hairline}`,
	bgcolor: planner.surfaceSubtle,
	color: planner.inkMuted,
	whiteSpace: "nowrap",
	flexShrink: 0,
} as const;

// Work-center chip — the same recipe the card used for its single work center,
// now one per work center the recipe touches.
export const workcenterChipSx = {
	display: "inline-flex",
	alignItems: "center",
	px: 1.25,
	py: 0.4,
	...planner.type.metaStrong,
	fontFamily: planner.fontUi,
	borderRadius: `${planner.radiusSm}px`,
	bgcolor: planner.accentSoft,
	border: `1px solid ${planner.accentBorder}`,
	color: planner.accentDeep,
	lineHeight: 1.4,
	whiteSpace: "nowrap",
} as const;

// Machine-time pill: neutral alloy, no role dot — occupancy, not a crew.
export const machineChipSx = {
	...planner.type.meta,
	fontFamily: planner.fontUi,
	display: "inline-flex",
	alignItems: "center",
	gap: 0.6,
	px: 1,
	py: 0.35,
	borderRadius: `${planner.radiusSm}px`,
	border: `1px dashed ${planner.hairlineStrong}`,
	bgcolor: planner.surfaceSubtle,
	color: planner.inkMuted,
	whiteSpace: "nowrap",
} as const;

// Sequence badge — same recipe everywhere: accentSoft fill, accentDeep mono figure.
export const sequenceBadgeSx = {
	...planner.type.metaStrong,
	fontFamily: planner.fontMono,
	width: 22,
	height: 22,
	borderRadius: "50%",
	bgcolor: planner.accentSoft,
	border: `1px solid ${planner.accentBorder}`,
	color: planner.accentDeep,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontVariantNumeric: "tabular-nums",
	flexShrink: 0,
} as const;

/** The quiet icon button on a card or an operation block. */
export const iconButtonSx = { color: planner.inkMuted, "&:hover": { color: planner.accent } } as const;
export const deleteIconButtonSx = { color: planner.inkMuted, "&:hover": { color: planner.unstaffed } } as const;
